import { EntityKind, fullName, type CreatureLook, type EntityState, type NpcLook } from "odyssey-shared";

/**
 * The name a page shows for an entity in its lines (P2.4-T2's review), the death line's killer among them: a character
 * by its first and family names, or by its first name alone where a chat line names it (ADR-068's amendment "chat
 * lines show the first name"), a creature by its look's name in the world's look table (ADR-082 decision 5), as the
 * target frame names it, and an NPC by its look's name in the world's NPC look table (ADR-136 decision 1). The chat's death line takes the first name; the death screen's cause takes the full name,
 * the one place a player reads the family of a killer who has left the view.
 */

/**
 * Which of a character's names a line shows: both, or the first alone as a chat line does.
 */
export type ShownNameForm = "full" | "first";

/**
 * An entity's shown name.
 *
 * @param state The entity as the page holds it, or undefined for one it does not.
 * @param looks The world's creature looks.
 * @param form Which of a character's names to show; a creature's or an NPC's name is the same in both.
 * @param npcLooks The world's NPC looks.
 * @returns The name, or null for an entity with none to show.
 * @example
 * shownName(rat, [{ name: "Mutant Rat", sheet: "mutant-rat" }]);
 * // "Mutant Rat"
 */
export function shownName(state: Pick<EntityState, "kind" | "name" | "appearance"> | undefined, looks: readonly CreatureLook[], form: ShownNameForm = "full", npcLooks: readonly NpcLook[] = []): string | null {
  if (state === undefined) {
    return null;
  }
  if (state.kind === EntityKind.Creature || state.kind === EntityKind.Npc) {
    const name = (state.kind === EntityKind.Npc ? npcLooks : looks)[state.appearance]?.name ?? "";
    return name === "" ? null : name;
  }
  if (state.name.first === "") {
    return null;
  }
  return form === "first" ? state.name.first : fullName(state.name.first, state.name.family);
}
